/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import PasswordChecklist from "react-password-checklist";
import { ERR_CONFIRM_PASSWORD_MESSAGE, ERR_NEW_PASSWORD_MESSAGE, LABEL_CONFIRM_PASSWORD, LABEL_NEW_PASSWORD, PLACEHOLDER_CONFIRM_PASSWORD, PLACEHOLDER_NEW_PASSWORD } from 'src/constants/constant';
import { InputAdornment, TextField } from '@mui/material';
import { changeUserPassword } from 'src/services/userService';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { NotificationManager } from 'react-notifications';

interface PasswordFormInputs {
  "password": string;
  "confirmPassword": string;
  "userId"?: number;
}

interface PropsType {
  closeModal: () => void;
  userId?: string;
}

interface ApiResponse {
  status: number;
  data: {
      result: number;
      messages: string;
  };
  
}

const validationSchema = Yup.object().shape({
  password: Yup.string().required(ERR_NEW_PASSWORD_MESSAGE),
  confirmPassword: Yup.string()
    .required(ERR_CONFIRM_PASSWORD_MESSAGE)
    .oneOf([Yup.ref("password")], "Confirm password should match new password"),
});

function ChangePassword({ closeModal, userId }: PropsType): JSX.Element {
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [confirmShowPassword, setConfirmShowPassword] = useState<boolean>(false)
  const [password, setPassword] = useState<string>("")
  const [confirmPassword, setConfirmPassword] = useState<string>("")

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    clearErrors
  } = useForm<PasswordFormInputs>({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const handlePasswordChange = (value: string): void => {
    setPassword(value);
    if (value === confirmPassword) {
      clearErrors('confirmPassword');
    } else {
      void trigger('confirmPassword');
    }
  };

  const handleConfirmPasswordChange = (value: string): void => {
    setConfirmPassword(value);
    if (value === password) {
      clearErrors('confirmPassword');
    } else {
      void trigger('confirmPassword');
    }
  };

  const onSubmit = async (data: PasswordFormInputs): Promise<void> => {
    setIsLoading(true)
    console.log(data)
    data.userId = userId !== undefined ? parseInt(userId) : 0
    changeUserPassword(data).then(
      (res: ApiResponse) => {
        console.log(res);
        if (res.status === 200) {
          if (res.data.result === 1) {
            NotificationManager.success("Password changed successfully")
            closeModal()
          } else {
            NotificationManager.error("Password not yet created")
          }
        }
      }
    )
      .catch(
        error => console.log(error)
      )
      .finally(
        () => setIsLoading(false)
      )
  }

  return (
    <React.Fragment>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
      >
        <div className="relative px-[16px] pt-[10px]">
          <div className="mb-[30px]">
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  autoComplete="off"
                  label={`${LABEL_NEW_PASSWORD}*`}
                  type={showPassword ? "text" : "password"}
                  placeholder={PLACEHOLDER_NEW_PASSWORD}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src="/assets/images/input-password.svg" alt="email" width={22} height={22} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <img src={showPassword ? "/assets/images/show-password.svg" : "/assets/images/close-password.svg"} onClick={() => setShowPassword(!showPassword)} alt="email" width={22} height={20} className="cursor-pointer" />
                      </InputAdornment>
                    )
                  }}
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e);
                    handlePasswordChange(e.target.value);
                  }}

                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                />
              )}
            />
          </div>
          <div className="mb-[30px]">
            <Controller
              name="confirmPassword"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  autoComplete="off"
                  label={`${LABEL_CONFIRM_PASSWORD}*`}
                  type={confirmShowPassword ? "text" : "password"}
                  placeholder={PLACEHOLDER_CONFIRM_PASSWORD}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src="/assets/images/input-password.svg" alt="email" width={22} height={22} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <img src={confirmShowPassword ? "/assets/images/show-password.svg" : "/assets/images/close-password.svg"} onClick={() => setConfirmShowPassword(!confirmShowPassword)} alt="email" width={22} height={20} className="cursor-pointer" />
                      </InputAdornment>
                    )
                  }}
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e);
                    handleConfirmPasswordChange(e.target.value);
                  }}
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword?.message}
                />
              )}
            />
          </div>
          <div >
            <PasswordChecklist
              rules={["minLength", "specialChar", "number", "capital", "match"]}
              minLength={8}
              value={password}
              valueAgain={confirmPassword}
              iconSize={10}
              className={"password-checklist"}
              invalidColor={"#FF0033"}
              iconComponents={{ ValidIcon: <img src="/assets/images/checklist-complete.svg" width={15} height={15} alt="" />, InvalidIcon: <img src="/assets/images/checklist.svg" width={10} height={10} alt="" /> }}
              messages={{
                minLength: "At least 8 characters long",
                specialChar: "One special character",
                number: "One number",
                capital: "One capital letter",
                match: "Confirm password should match new password",
              }}
              
            />
          </div>
        </div>
        <div className="flex items-center fixed left-0 bottom-0 w-[100%]">
          <button type="button" className="w-[50%] bg-white h-[55px] border border-inputborder text-muted flex font-semibold text-[15px] items-center justify-center" disabled={isLoading} onClick={closeModal}>
            Cancel
          </button>
          <button type="submit" className="w-[50%] h-[55px] bg-gradient-to-b border-0 from-[#ED4B52] to-[#D6242C] h-[55px text-white flex font-semibold text-[15px] items-center justify-center" disabled={isLoading}>
            {!isLoading ? 'Save' : <span className="loading loading-spinner loading-xs text-white"></span>}
          </button>
        </div>
      </form>
    </React.Fragment>
  )
}

export default ChangePassword